// Client-side port of /leaderboard/rows. Mirrors top_skaters_on_ice in
// src/pwhlstats/web/leaderboard.py — same SQL, same parameter order,
// same sort keys, same result shape. Renders into the page's
// #lb-results container, matching the structure of
// templates/_leaderboard_rows.html so the existing CSS keeps working.

import { getDbWorker } from "./db";
import { STRENGTH_STATES, strengthPredForSkater } from "./filters";

const MIN_EVENTS_CHOICES = [20, 50, 100, 200];
export const SORT_KEYS: Record<string, string> = {
  xg_diff: "(xgf - xga) DESC",
  xgf_pct: "xgf_pct DESC",
  xgf: "xgf DESC",
  xga: "xga ASC",
  events: "events DESC",
  toi: "toi_seconds DESC",
  gf: "gf DESC",
  ga: "ga ASC",
  name: "name COLLATE NOCASE ASC",
};

// Same labels as web/app.py::_STRENGTH_LABELS.
const STRENGTH_LABELS: Record<string, string> = {
  "": "any strength",
  "5v5": "5v5",
  pp: "PP",
  pk: "PK",
  "4v4": "4v4",
  "3v3": "3v3",
  en: "empty net",
};

interface LeaderboardRow {
  player_id: number;
  name: string;
  team_code: string | null;
  events: number;
  gf: number;
  ga: number;
  xgf: number;
  xga: number;
  xgf_pct: number | null;
  toi_seconds: number;
}

interface LeaderboardFilters {
  strength_state: string;
  team_id: number | null;
  min_events: number;
  sort_by: string;
  season_type: string | null;
}

function buildSql(filters: LeaderboardFilters): { sql: string; params: (number | string)[] } {
  const params: (number | string)[] = [];
  const ss = filters.strength_state;
  const strengthPred =
    ss && ss in STRENGTH_STATES
      ? `AND ${strengthPredForSkater(ss) ?? ""}`.replace(/AND $/, "")
      : "";
  let seasonPred = "";
  if (filters.season_type === "regular") {
    seasonPred = "AND (g.season_type = 'regular' OR g.season_type IS NULL)";
  } else if (
    filters.season_type === "playoff" ||
    filters.season_type === "preseason"
  ) {
    seasonPred = `AND g.season_type = '${filters.season_type}'`;
  }
  let teamFilterSql = "";
  if (filters.team_id !== null) {
    teamFilterSql =
      "AND ((oi.side = 'home'    AND oi.home_team_id = ?) OR " +
      "     (oi.side = 'visitor' AND oi.away_team_id = ?))";
    params.push(filters.team_id, filters.team_id);
  }
  params.push(filters.min_events);
  const orderSql = SORT_KEYS[filters.sort_by] ?? SORT_KEYS["xg_diff"]!;
  params.push(100); // limit matches the Python default

  const sql = `
    WITH goalies AS (
        SELECT DISTINCT goalie_id AS player_id FROM events
        WHERE goalie_id IS NOT NULL AND goalie_id != 0
    ),
    shift_toi AS (
        SELECT player_id, SUM(end_seconds - start_seconds) AS toi_seconds
        FROM shifts
        GROUP BY player_id
    ),
    on_ice AS (
        SELECT
            s.player_id, s.side,
            g.home_team_id, g.away_team_id,
            e.team_id AS ev_team, e.xg, e.is_goal
        FROM shifts s
        JOIN events e ON e.game_id = s.game_id
                     AND e.clock_seconds_elapsed >= s.start_seconds
                     AND e.clock_seconds_elapsed <  s.end_seconds
        JOIN games  g ON g.game_id = e.game_id
        WHERE e.event_type IN ('shot','goal')
          AND e.xg IS NOT NULL
          AND s.player_id NOT IN (SELECT player_id FROM goalies)
          ${strengthPred}
          ${seasonPred}
    ),
    agg AS (
        SELECT
            oi.player_id,
            COUNT(*) AS events,
            SUM(CASE WHEN is_goal AND
                        ((side='home'    AND ev_team = home_team_id) OR
                         (side='visitor' AND ev_team = away_team_id))
                     THEN 1 ELSE 0 END) AS gf,
            SUM(CASE WHEN is_goal AND
                        ((side='home'    AND ev_team = away_team_id) OR
                         (side='visitor' AND ev_team = home_team_id))
                     THEN 1 ELSE 0 END) AS ga,
            SUM(CASE WHEN (side='home'    AND ev_team = home_team_id) OR
                          (side='visitor' AND ev_team = away_team_id)
                     THEN xg ELSE 0 END) AS xgf,
            SUM(CASE WHEN (side='home'    AND ev_team = away_team_id) OR
                          (side='visitor' AND ev_team = home_team_id)
                     THEN xg ELSE 0 END) AS xga,
            MAX(CASE WHEN oi.side='home' THEN oi.home_team_id ELSE oi.away_team_id END) AS team_id
        FROM on_ice oi
        WHERE 1=1 ${teamFilterSql}
        GROUP BY oi.player_id
        HAVING events >= ?
    )
    SELECT
        a.player_id,
        p.first_name || ' ' || p.last_name AS name,
        t.code AS team_code,
        a.events, a.gf, a.ga, a.xgf, a.xga,
        (a.xgf / NULLIF(a.xgf + a.xga, 0)) AS xgf_pct,
        COALESCE(st.toi_seconds, 0) AS toi_seconds
    FROM agg a
    JOIN players p ON p.player_id = a.player_id
    LEFT JOIN teams t ON t.team_id = a.team_id
    LEFT JOIN shift_toi st ON st.player_id = a.player_id
    ORDER BY ${orderSql}
    LIMIT ?
  `;
  return { sql, params };
}

function escapeHtml(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Short stat definition for a glossary slug, from the shared global injected
// by base.html (source: stats_glossary.py). Empty string when unknown.
function glossShort(slug: string): string {
  const g = (window as unknown as { __PWHL_GLOSS__?: Record<string, string> }).__PWHL_GLOSS__;
  return (g && g[slug]) || "";
}

function sortHeader(
  label: string,
  key: string,
  current: string,
  align = "num",
  gkey?: string,
): string {
  const active = current === key ? "sort-active" : "";
  const caret = current === key ? " ▾" : "";
  const tip = gkey ? glossShort(gkey) : "";
  const glossClass = tip ? " gloss-th" : "";
  const glossAttr = tip ? ` data-glossary="${escapeHtml(tip)}"` : "";
  return `<th class="${align} sortable${glossClass} ${active}" data-sort-key="${key}"${glossAttr} role="button" tabindex="0">${escapeHtml(label)}${caret}</th>`;
}

function fmtSigned(n: number, decimals = 2): string {
  const sign = n >= 0 ? "+" : "";
  return `${sign}${n.toFixed(decimals)}`;
}

function fmtToi(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function renderLeaderboard(
  rows: LeaderboardRow[],
  filters: LeaderboardFilters,
): string {
  const strengthLabel = STRENGTH_LABELS[filters.strength_state] ?? "any strength";
  if (rows.length === 0) {
    return '<p class="empty">No skaters meet those thresholds — try a lower "Min events on-ice".</p>';
  }
  const head = `
    <p class="result-meta">
      Top ${rows.length} skaters (${escapeHtml(strengthLabel)}) — ranked by xGF − xGA.
      Skaters need ≥ ${filters.min_events} shots on-ice to appear.
      Click a player to see their shot map; click a column header to sort.
    </p>
    <table class="results leaderboard standalone">
      <thead><tr>
        <th class="rank">#</th>
        ${sortHeader("Player", "name", filters.sort_by, "text")}
        <th class="text">Team</th>
        ${sortHeader("TOI", "toi", filters.sort_by, "num", "toi")}
        ${sortHeader("Events", "events", filters.sort_by, "num", "events")}
        ${sortHeader("GF", "gf", filters.sort_by, "num", "gf")}
        ${sortHeader("GA", "ga", filters.sort_by, "num", "ga")}
        ${sortHeader("xGF", "xgf", filters.sort_by, "num", "xgf")}
        ${sortHeader("xGA", "xga", filters.sort_by, "num", "xga")}
        ${sortHeader("xGF%", "xgf_pct", filters.sort_by, "num", "xgf_pct")}
        ${sortHeader("xG diff (xGF − xGA)", "xg_diff", filters.sort_by, "num", "xg_diff")}
      </tr></thead>
      <tbody>
  `;
  const body = rows
    .map((r, i) => {
      const xgfPct = (r.xgf_pct ?? 0) * 100;
      const diff = r.xgf - r.xga;
      const diffClass = diff >= 0 ? "pos" : "neg";
      return `
        <tr>
          <td class="rank">${i + 1}</td>
          <td><a class="skater-link" href="/play-index/?event_group=shots_on_net&player_id=${r.player_id}">${escapeHtml(r.name)}</a></td>
          <td>${escapeHtml(r.team_code) || "—"}</td>
          <td class="num">${fmtToi(r.toi_seconds)}</td>
          <td class="num">${r.events}</td>
          <td class="num">${r.gf}</td>
          <td class="num">${r.ga}</td>
          <td class="num">${r.xgf.toFixed(2)}</td>
          <td class="num">${r.xga.toFixed(2)}</td>
          <td class="num">${xgfPct.toFixed(1)}</td>
          <td class="num ${diffClass}">${fmtSigned(diff)}</td>
        </tr>
      `;
    })
    .join("");
  return `${head}${body}</tbody></table>`;
}

export function readLeaderboardFilters(form: HTMLFormElement): LeaderboardFilters {
  const val = (name: string): string =>
    (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null)?.value ?? "";
  const intOrNull = (s: string): number | null => {
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };
  const minEventsRaw = intOrNull(val("min_events")) ?? 50;
  const minEvents = MIN_EVENTS_CHOICES.includes(minEventsRaw) ? minEventsRaw : 50;
  const ss = val("strength_state");
  const sortRaw = val("sort_by") || "xg_diff";
  const sortBy = sortRaw in SORT_KEYS ? sortRaw : "xg_diff";
  const seasonType = val("season_type");
  return {
    strength_state: ss in STRENGTH_STATES || ss === "" ? ss : "5v5",
    team_id: intOrNull(val("team_id")),
    min_events: minEvents,
    sort_by: sortBy,
    season_type:
      seasonType === "regular" ||
      seasonType === "playoff" ||
      seasonType === "preseason"
        ? seasonType
        : null,
  };
}

export async function runAndRender(
  form: HTMLFormElement,
  results: HTMLElement,
  filters: LeaderboardFilters,
): Promise<void> {
  results.innerHTML = '<p class="empty">Loading…</p>';
  try {
    const worker = await getDbWorker();
    const { sql, params } = buildSql(filters);
    const rows = (await worker.db.query(sql, params)) as LeaderboardRow[];
    results.innerHTML = renderLeaderboard(rows, filters);
    wireSortHeaders(form, results);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    results.innerHTML = `<p class="empty">Query failed: ${escapeHtml(msg)}</p>`;
    console.error("[leaderboard] query failed:", err);
  }
}

function wireSortHeaders(form: HTMLFormElement, results: HTMLElement): void {
  for (const th of results.querySelectorAll<HTMLElement>("th[data-sort-key]")) {
    th.addEventListener("click", () => {
      // Stash the sort key in a hidden field so the form-read picks it up.
      let hidden = form.querySelector<HTMLInputElement>('input[name="sort_by"]');
      if (!hidden) {
        hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = "sort_by";
        form.appendChild(hidden);
      }
      hidden.value = th.dataset["sortKey"]!;
      const filters = readLeaderboardFilters(form);
      void runAndRender(form, results, filters);
    });
  }
}

export function initLeaderboard(): boolean {
  const form = document.getElementById("lb-filters") as HTMLFormElement | null;
  const results = document.getElementById("lb-results");
  if (!form || !results) return false;

  const refilter = (): void => {
    const filters = readLeaderboardFilters(form);
    void runAndRender(form, results, filters);
  };
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    refilter();
  });
  form.addEventListener("change", (e) => {
    if (e.target instanceof HTMLSelectElement) refilter();
  });
  refilter();
  return true;
}
